import Swiper from 'swiper';
import { Navigation, Scrollbar } from 'swiper/modules';
import {
	WP_HERO_BAND_CREATIVE_SLOT,
	trackSelectPromotion,
	trackViewPromotion,
} from '../../src/js/lifted-promo-analytics.js';

const promotionsCache = new Map();
const moduleHydrationState = new WeakMap();
const viewedPromotionIds = new Set();
const HERO_PROMO_VIEW_THRESHOLD = 0.5;

function buildPromoTrackInput(promotion) {
	if (!promotion || promotion.id == null || promotion.id === '') {
		return null;
	}

	const promotionName = (promotion.title || '').trim();

	if (!promotionName) {
		return null;
	}

	const creativeName =
		(promotion.image && promotion.image.alt) ||
		(promotion.overline || '').trim() ||
		undefined;

	return {
		promotionId: String(promotion.id),
		promotionName,
		creativeName,
		creativeSlot: WP_HERO_BAND_CREATIVE_SLOT,
	};
}

function isDebugEnabled() {
	return (
		(window.liftedHeroBandConfig && window.liftedHeroBandConfig.debug) ||
		window.localStorage.getItem('heroBandDebug') === '1'
	);
}

function debugLog(...args) {
	if (!isDebugEnabled()) {
		return;
	}

	console.log('[HeroBand]', ...args);
}

function setModuleState(module, state, detail = {}) {
	module.dataset.heroBandState = state;

	if (isDebugEnabled()) {
		debugLog('state:', state, detail);
	}
}

function buildBreakpoints(swiperEl) {
	const columns = swiperEl.dataset.sliderColumns;
	const desktopSlidesPerView = swiperEl.dataset.sliderColumnsDesktop;
	const tabletSlidesPerView = swiperEl.dataset.sliderColumnsTablet;
	const mobileSlidesPerView = swiperEl.dataset.sliderColumnsMobile;

	let breakpoints = {
		320: {
			slidesPerView: mobileSlidesPerView ? mobileSlidesPerView : 1,
			spaceBetween: 8,
		},
		640: {
			slidesPerView: tabletSlidesPerView ? tabletSlidesPerView : 2,
			spaceBetween: 8,
		},
		1024: {
			slidesPerView: desktopSlidesPerView ? desktopSlidesPerView : 3,
			spaceBetween: 16,
		},
	};

	if ((columns && columns == '4') || desktopSlidesPerView == '4') {
		breakpoints = {
			320: {
				slidesPerView: mobileSlidesPerView ? mobileSlidesPerView : 1.1,
				spaceBetween: 8,
			},
			500: {
				slidesPerView: tabletSlidesPerView ? tabletSlidesPerView : 2,
			},
			640: {
				slidesPerView: tabletSlidesPerView ? tabletSlidesPerView : 3,
			},
			1024: {
				slidesPerView: desktopSlidesPerView ? desktopSlidesPerView : 4,
				spaceBetween: 16,
			},
		};
	}

	if ((columns && columns == '2') || desktopSlidesPerView == '2') {
		breakpoints = {
			640: {
				slidesPerView: desktopSlidesPerView ? desktopSlidesPerView : 2,
				spaceBetween: 16,
			},
		};
	}

	if ((columns && columns == '1') || desktopSlidesPerView == '1') {
		breakpoints = {
			640: {
				slidesPerView: desktopSlidesPerView ? desktopSlidesPerView : 1,
				spaceBetween: 16,
			},
		};
	}

	return breakpoints;
}

function getSelectedRetailer() {
	const getCookie = window.liftedEcommGetCookie;
	const findRetailer = window.liftedEcommFindRetailerRow;

	if (typeof getCookie !== 'function' || typeof findRetailer !== 'function') {
		debugLog('retailer helpers unavailable', {
			hasGetCookie: typeof getCookie === 'function',
			hasFindRetailer: typeof findRetailer === 'function',
		});
		return null;
	}

	const retailerId = getCookie('retailerID');

	if (!retailerId) {
		debugLog('no retailerID cookie');
		return null;
	}

	const retailer = findRetailer(retailerId);

	if (!retailer) {
		debugLog('retailerID cookie did not match retailerData', { retailerId });
		return null;
	}

	return retailer;
}

function getShopPageUrl() {
	if (typeof retailerData === 'undefined') {
		return '';
	}

	return retailerData.shop_page_url || '';
}

function resolveButtonHref(button) {
	if (!button || typeof button !== 'object') {
		return null;
	}

	if (button.type === 'link' && button.link && button.link.url) {
		return button.link.url;
	}

	if (button.type === 'sdk_action') {
		const shopPageUrl = getShopPageUrl();
		const sdkLinks = window.liftedEcommSdkActionLinks || {};

		if (typeof sdkLinks.resolveSdkActionLink === 'function') {
			const resolved = sdkLinks.resolveSdkActionLink(
				button.action,
				button.params || {},
				shopPageUrl
			);

			if (resolved) {
				return resolved;
			}
		}

		return shopPageUrl || null;
	}

	return null;
}

function resolveButtonLabel(button) {
	if (!button || typeof button !== 'object') {
		return '';
	}

	if (button.label) {
		return button.label;
	}

	if (button.type === 'link' && button.link && button.link.title) {
		return button.link.title;
	}

	return '';
}

function resolveButtonTarget(button) {
	if (button && button.type === 'link' && button.link && button.link.target) {
		return button.link.target;
	}

	return '_self';
}

function applyRetailerLinksToContainer(container) {
	if (!container) {
		return;
	}

	const retailer = getSelectedRetailer();

	if (!retailer) {
		return;
	}

	const getCookie = window.liftedEcommGetCookie;
	const retailerId = typeof getCookie === 'function' ? getCookie('retailerID') : null;
	const retailerMenuType = localStorage.getItem('cp_retailer_menu_type');
	const hasShopUrl =
		typeof window.liftedEcommRetailerHasShopUrl === 'function'
			? window.liftedEcommRetailerHasShopUrl(retailer)
			: !!(retailer.rec_url || retailer.med_url);
	const shopLinks = window.liftedEcommShopLinks || {};
	const shopPageUrl = getShopPageUrl();
	const retailers = (typeof retailerData !== 'undefined' && retailerData.retailers) || [];
	let linkIndex = 0;

	let retailerUrl = shopLinks.resolveRetailerMenuUrl
		? shopLinks.resolveRetailerMenuUrl(retailer, retailerMenuType)
		: '';

	if (!retailerUrl) {
		if (retailerMenuType === 'RECREATIONAL') {
			retailerUrl = retailer.rec_url || '';
		} else if (retailerMenuType === 'MEDICAL') {
			retailerUrl = retailer.med_url || '';
		}
		retailerUrl = String(retailerUrl || '')
			.trim()
			.replace(/\/$/, '');
	}

	container.querySelectorAll('a[href]').forEach((link) => {
		const href = link.getAttribute('href');

		if (!href) {
			return;
		}

		if (!link.dataset.originalShopHref) {
			link.dataset.originalShopHref = href;
		}

		if (!hasShopUrl || !shopPageUrl || !retailerUrl) {
			return;
		}

		const originalHref = link.dataset.originalShopHref;
		const updatedHref =
			typeof shopLinks.rewriteDutchieShopHref === 'function'
				? shopLinks.rewriteDutchieShopHref({
						originalHref,
						shopUrl: shopPageUrl,
						retailerUrl,
						pageOrigin: window.location.origin,
						retailers,
						pageHostname: window.location.hostname,
					})
				: null;

		if (!updatedHref) {
			return;
		}

		if (!link.hasAttribute('data-retailer-link')) {
			link.setAttribute('data-retailer-link', `hero-band-retailer-link-${linkIndex}`);
			linkIndex += 1;
		}

		if (retailerId) {
			link.setAttribute('data-retailer-id', retailerId);
		}

		link.href = updatedHref;
	});
}

function createPromotionImage(image, title) {
	if (!image || !image.url) {
		return null;
	}

	const media = document.createElement('div');
	media.className = 'hero-band__promotion-media';

	const img = document.createElement('img');
	img.src = image.url;
	img.alt = image.alt || title || '';
	img.loading = 'lazy';

	if (image.srcset) {
		img.srcset = image.srcset;
	}

	if (image.sizes_attr) {
		img.sizes = image.sizes_attr;
	}

	media.appendChild(img);

	return media;
}

function createPromotionButtons(buttons, promoTrackInput) {
	const wrapper = document.createElement('div');
	wrapper.className = 'hero-band__promotion-buttons';

	if (!Array.isArray(buttons) || !buttons.length) {
		wrapper.hidden = true;
		return wrapper;
	}

	buttons.forEach((button) => {
		const href = resolveButtonHref(button);
		const label = resolveButtonLabel(button);
		const style = button.style || 'primary';

		if (!label) {
			return;
		}

		const link = document.createElement('a');
		link.className = `btn btn-${style}`;
		link.textContent = label;

		if (href) {
			link.href = href;
			link.target = resolveButtonTarget(button);

			if (button.type === 'link' && button.link && button.link.target === '_blank') {
				link.rel = 'noopener noreferrer';
			}
		} else {
			link.setAttribute('role', 'button');
		}

		if (promoTrackInput) {
			link.addEventListener('click', () => {
				trackSelectPromotion(promoTrackInput);
			});
		}

		wrapper.appendChild(link);
	});

	if (!wrapper.children.length) {
		wrapper.hidden = true;
	}

	return wrapper;
}

function createPromotionSlide(promotion) {
	const slide = document.createElement('div');
	slide.className = 'swiper-slide';

	const card = document.createElement('article');
	card.className = 'hero-band__promotion-card';

	const promoTrackInput = buildPromoTrackInput(promotion);

	if (promoTrackInput) {
		card.dataset.promotionId = promoTrackInput.promotionId;
		card.dataset.promotionName = promoTrackInput.promotionName;
		if (promoTrackInput.creativeName) {
			card.dataset.creativeName = promoTrackInput.creativeName;
		}
		card.dataset.creativeSlot = promoTrackInput.creativeSlot;
	}

	const cardStyles = [];

	if (promotion.background_color) {
		cardStyles.push(`background-color:${promotion.background_color}`);
		cardStyles.push(`--hero-band-promotion-bg-color:${promotion.background_color}`);
	}

	if (promotion.background_image && promotion.background_image.url) {
		cardStyles.push(`background-image:url(${promotion.background_image.url})`);
	}

	if (promotion.text_color) {
		cardStyles.push(`color:${promotion.text_color}`);
		cardStyles.push(`--hero-band-promotion-text-color:${promotion.text_color}`);
	}

	if (cardStyles.length) {
		card.setAttribute('style', cardStyles.join(';'));
	}

	const productImage = createPromotionImage(promotion.image, promotion.title);

	if (productImage) {
		card.appendChild(productImage);
	}

	const copy = document.createElement('div');
	copy.className = 'hero-band__promotion-copy';

	if (promotion.overline) {
		const overline = document.createElement('p');
		overline.className = 'hero-band__promotion-overline';
		overline.textContent = promotion.overline;
		if (promotion.text_color) {
			overline.style.color = promotion.text_color;
		}
		copy.appendChild(overline);
	}

	if (promotion.title) {
		const title = document.createElement('h3');
		title.className = 'hero-band__promotion-title';
		title.textContent = promotion.title;
		if (promotion.text_color) {
			title.style.color = promotion.text_color;
		}
		copy.appendChild(title);
	}

	if (promotion.description) {
		const description = document.createElement('p');
		description.className = 'hero-band__promotion-description';
		description.textContent = promotion.description;
		if (promotion.text_color) {
			description.style.color = promotion.text_color;
		}
		copy.appendChild(description);
	}

	copy.appendChild(createPromotionButtons(promotion.buttons, promoTrackInput));
	card.appendChild(copy);
	slide.appendChild(card);

	return slide;
}

function observePromotionImpressions(container) {
	if (!container || typeof IntersectionObserver === 'undefined') {
		return;
	}

	const cards = container.querySelectorAll('.hero-band__promotion-card[data-promotion-id]');

	if (!cards.length) {
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting || entry.intersectionRatio < HERO_PROMO_VIEW_THRESHOLD) {
					return;
				}

				const card = entry.target;
				const promotionId = card.dataset.promotionId;

				if (!promotionId || viewedPromotionIds.has(promotionId)) {
					observer.unobserve(card);
					return;
				}

				viewedPromotionIds.add(promotionId);
				trackViewPromotion({
					promotionId,
					promotionName: card.dataset.promotionName,
					creativeName: card.dataset.creativeName || undefined,
					creativeSlot: card.dataset.creativeSlot || WP_HERO_BAND_CREATIVE_SLOT,
				});
				observer.unobserve(card);
			});
		},
		{ threshold: HERO_PROMO_VIEW_THRESHOLD }
	);

	cards.forEach((card) => observer.observe(card));
}

const SKELETON_SLIDE_COUNT = 6;
const SKELETON_SLIDE_HTML = `
	<article class="hero-band__promotion-card hero-band__skeleton-card">
		<div class="hero-band__promotion-media">
			<div class="hero-band__skeleton-block hero-band__skeleton-block--media"></div>
		</div>
		<div class="hero-band__promotion-copy">
			<div class="hero-band__skeleton-block hero-band__skeleton-block--overline"></div>
			<div class="hero-band__skeleton-block hero-band__skeleton-block--title"></div>
			<div class="hero-band__skeleton-block hero-band__skeleton-block--description"></div>
			<div class="hero-band__promotion-buttons">
				<div class="hero-band__skeleton-block hero-band__skeleton-block--button"></div>
				<div class="hero-band__skeleton-block hero-band__skeleton-block--button"></div>
			</div>
		</div>
	</article>
`;

function renderSkeletonSlides(wrapper) {
	wrapper.innerHTML = '';

	for (let i = 0; i < SKELETON_SLIDE_COUNT; i += 1) {
		const slide = document.createElement('div');
		slide.className = 'swiper-slide hero-band__skeleton-slide';
		slide.setAttribute('aria-hidden', 'true');
		slide.innerHTML = SKELETON_SLIDE_HTML;
		wrapper.appendChild(slide);
	}
}

function showSkeletonSlides(module, swiperEl, wrapper) {
	renderSkeletonSlides(wrapper);
	initSwiper(module, swiperEl);
}

async function fetchPromotions(restUrl, dutchieCname) {
	const cacheKey = dutchieCname || '__all__';

	if (promotionsCache.has(cacheKey)) {
		debugLog('promotions cache hit', { dutchieCname, count: promotionsCache.get(cacheKey).length });
		return promotionsCache.get(cacheKey);
	}

	const url = new URL(restUrl, window.location.origin);

	if (dutchieCname) {
		url.searchParams.set('dutchie_cname', dutchieCname);
	}

	debugLog('fetching promotions', url.toString());

	const response = await fetch(url.toString(), {
		headers: {
			Accept: 'application/json',
		},
	});

	if (!response.ok) {
		throw new Error(`Promotions request failed: ${response.status}`);
	}

	const data = await response.json();
	const promotions = Array.isArray(data.promotions) ? data.promotions : [];

	debugLog('promotions fetched', { dutchieCname, count: promotions.length });

	promotionsCache.set(cacheKey, promotions);

	return promotions;
}

function initSwiper(module, swiperEl) {
	const next = module.querySelector('.slider-nav-next');
	const prev = module.querySelector('.slider-nav-prev');
	const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');
	const totalSlides = swiperEl.querySelectorAll('.swiper-slide').length;

	if (swiperEl.swiper) {
		swiperEl.swiper.destroy(true, true);
	}

	if (totalSlides <= 1) {
		if (next) {
			next.style.display = 'none';
		}
		if (prev) {
			prev.style.display = 'none';
		}
	} else {
		if (next) {
			next.style.display = '';
		}
		if (prev) {
			prev.style.display = '';
		}
	}

	const swiper = new Swiper(swiperEl, {
		modules: [Navigation, Scrollbar],
		slidesPerView: 1,
		spaceBetween: 8,
		navigation: {
			nextEl: next,
			prevEl: prev,
		},
		scrollbar: {
			el: scrollbarEl,
			draggable: true,
			hide: false,
		},
		breakpoints: buildBreakpoints(swiperEl),
	});

	if (swiper.scrollbar) {
		swiper.scrollbar.updateSize();
	}

	return swiper;
}

function setPromotionsBusy(promotionsEl, isBusy) {
	promotionsEl.setAttribute('aria-busy', isBusy ? 'true' : 'false');
}

async function hydrateHeroBand(module) {
	const promotionsEl = module.querySelector('.hero-band__promotions');
	const swiperEl = module.querySelector('.swiper');
	const wrapper = module.querySelector('.swiper-wrapper');
	const restUrl = module.dataset.restUrl;
	const retailer = getSelectedRetailer();
	const dutchieCname = retailer?.dutchie_cname || '';
	const previousState = moduleHydrationState.get(module) || {};

	if (previousState.hydrating) {
		debugLog('skip hydrate: already in progress', { dutchieCname });
		return;
	}

	if (
		previousState.dutchieCname === dutchieCname &&
		previousState.state === 'loaded' &&
		module.dataset.heroBandState === 'loaded'
	) {
		debugLog('skip hydrate: already loaded for cname', { dutchieCname });
		return;
	}

	moduleHydrationState.set(module, {
		hydrating: true,
		dutchieCname,
		state: previousState.state || null,
	});

	try {
		if (!promotionsEl || !swiperEl || !wrapper || !restUrl) {
			setModuleState(module, 'error-missing-markup', {
				hasPromotionsEl: !!promotionsEl,
				hasSwiperEl: !!swiperEl,
				hasWrapper: !!wrapper,
				restUrl: restUrl || null,
			});
			return;
		}

		if (!retailer) {
			showSkeletonSlides(module, swiperEl, wrapper);
			promotionsEl.classList.remove('is-empty');
			setPromotionsBusy(promotionsEl, true);
			setModuleState(module, 'waiting-for-location');

			moduleHydrationState.set(module, { hydrating: false, dutchieCname: '', state: 'waiting-for-location' });
			return;
		}

		if (!retailer.dutchie_cname) {
			showSkeletonSlides(module, swiperEl, wrapper);
			promotionsEl.classList.remove('is-empty');
			setPromotionsBusy(promotionsEl, true);
			setModuleState(module, 'missing-dutchie-cname', {
				retailerId: retailer.id,
				retailerName: retailer.name,
			});

			moduleHydrationState.set(module, { hydrating: false, dutchieCname: '', state: 'missing-dutchie-cname' });
			return;
		}

		setPromotionsBusy(promotionsEl, true);
		showSkeletonSlides(module, swiperEl, wrapper);
		setModuleState(module, 'loading', {
			retailerId: retailer.id,
			dutchieCname: retailer.dutchie_cname,
		});

		const promotions = await fetchPromotions(restUrl, retailer.dutchie_cname);

		if (!promotions.length) {
			wrapper.innerHTML = '';
			promotionsEl.classList.add('is-empty');
			setPromotionsBusy(promotionsEl, false);
			setModuleState(module, 'empty', { dutchieCname: retailer.dutchie_cname });

			if (swiperEl.swiper) {
				swiperEl.swiper.destroy(true, true);
			}

			moduleHydrationState.set(module, {
				hydrating: false,
				dutchieCname: retailer.dutchie_cname,
				state: 'empty',
			});
			return;
		}

		promotionsEl.classList.remove('is-empty');
		wrapper.innerHTML = '';

		promotions.forEach((promotion) => {
			wrapper.appendChild(createPromotionSlide(promotion));
		});

		applyRetailerLinksToContainer(wrapper);
		initSwiper(module, swiperEl);
		observePromotionImpressions(wrapper);
		setPromotionsBusy(promotionsEl, false);
		setModuleState(module, 'loaded', {
			dutchieCname: retailer.dutchie_cname,
			count: promotions.length,
		});

		moduleHydrationState.set(module, {
			hydrating: false,
			dutchieCname: retailer.dutchie_cname,
			state: 'loaded',
		});
	} catch (error) {
		if (promotionsEl && wrapper && swiperEl) {
			showSkeletonSlides(module, swiperEl, wrapper);
			promotionsEl.classList.remove('is-empty');
			setPromotionsBusy(promotionsEl, true);
		}

		setModuleState(module, 'error', {
			dutchieCname,
			message: error && error.message ? error.message : String(error),
		});
		debugLog('fetch failed', error);

		moduleHydrationState.set(module, {
			hydrating: false,
			dutchieCname,
			state: 'error',
		});
	}
}

function hydrateAllHeroBands() {
	const modules = document.querySelectorAll('.module-hero-band');

	debugLog('hydrateAllHeroBands', { moduleCount: modules.length, readyState: document.readyState });

	modules.forEach((module) => {
		hydrateHeroBand(module);
	});
}

function bindHeroBandEvents() {
	document.addEventListener('retailerUpdate', hydrateAllHeroBands);
	window.addEventListener('retailerUpdated', hydrateAllHeroBands);
}

function initHeroBand() {
	bindHeroBandEvents();

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', hydrateAllHeroBands);
		return;
	}

	hydrateAllHeroBands();
}

if (isDebugEnabled()) {
	window.liftedHeroBandDebug = {
		hydrateAll: hydrateAllHeroBands,
		enable: () => window.localStorage.setItem('heroBandDebug', '1'),
		disable: () => window.localStorage.removeItem('heroBandDebug'),
	};
	debugLog('debug helpers available via window.liftedHeroBandDebug');
}

initHeroBand();
